/**
 * The print taxonomy for the central printing service.
 *
 * `PrintType` enumerates every document the app can print; `PrintPayloads` maps
 * each type to the typed data it needs. `printingService.print(type, payload)`
 * narrows the payload from the type, and the renderer registry is keyed by the
 * same enum so a missing renderer is a compile error.
 */
import type {
  ProductDataType,
  CheckoutDataType,
  DailySummaryType,
  SaleOrderDetails,
  BusinessDataV2Type,
  DailySummaryByPeriod,
} from '@am3nd-org/pointcraft-types';

import type { OnlinePaymentReceiptData } from 'src/online-payment/utils/transactionToReceipt';

import type { ItemLabelData, KitchenTicketOrder, OrderReceiptVariant } from './renderers/types';

export enum PrintType {
  /** Customer sales receipt (Square-style, with QR + branding). */
  SALE_RECEIPT = 'sale_receipt',
  /** Back-of-house kitchen order ticket (no prices). */
  KITCHEN_TICKET = 'kitchen_ticket',
  /** Single-day sales summary report. */
  DAILY_SUMMARY = 'daily_summary',
  /** Monthly sales summary report. */
  PERIOD_SUMMARY = 'period_summary',
  /** Receipt for a bulk inventory-add event. */
  INVENTORY_RECEIPT = 'inventory_receipt',
  /** Receipt for an online / payment-link transaction. */
  ONLINE_PAYMENT_RECEIPT = 'online_payment_receipt',
  /** A single food/drink sticky label (one per item), printed sideways on a label roll. */
  ITEM_LABEL = 'item_label',
  /** Printer test page (validates the bitmap pipeline). */
  TEST = 'test',
}

export interface PrintPayloads {
  [PrintType.SALE_RECEIPT]: {
    checkout: CheckoutDataType;
    business: BusinessDataV2Type;
    /** Receipt-only metadata; kept outside the financial checkout/API payload. */
    orderDetails?: SaleOrderDetails | null;
  };
  [PrintType.KITCHEN_TICKET]: { order: KitchenTicketOrder; variant?: OrderReceiptVariant };
  [PrintType.DAILY_SUMMARY]: {
    summary: DailySummaryType | DailySummaryByPeriod;
    business: BusinessDataV2Type;
    /** ISO date key for the day selected on the reports page. */
    reportDate: string;
  };
  [PrintType.PERIOD_SUMMARY]: {
    summary: DailySummaryType | DailySummaryByPeriod;
    business: BusinessDataV2Type;
    /** Human-readable month selected on the reports page, e.g. "August 2026". */
    monthLabel: string;
    /** API-backed range represented by the monthly totals. */
    dateRange: { start: string; end: string };
  };
  [PrintType.INVENTORY_RECEIPT]: {
    items: ProductDataType[];
    business: BusinessDataV2Type;
    operator?: string;
    deviceId?: string;
  };
  [PrintType.ONLINE_PAYMENT_RECEIPT]: { data: OnlinePaymentReceiptData };
  [PrintType.ITEM_LABEL]: ItemLabelData;
  [PrintType.TEST]: { business?: BusinessDataV2Type };
}

export type PrintPayload<T extends PrintType = PrintType> = PrintPayloads[T];

/** One typed document in a heterogeneous print batch. */
export type PrintDocument = {
  [T in PrintType]: { type: T; payload: PrintPayloads[T] };
}[PrintType];

/** A print batch must contain at least one document. */
export type NonEmptyPrintDocuments = readonly [PrintDocument, ...PrintDocument[]];

/**
 * Outcome of a `print` call. `cancelled` means the user dismissed the picker.
 * `outcome` says what the driver knows about delivery: `'submitted'` (every
 * byte reached the printer), `'not-sent'` (nothing left the device; a retry
 * is safe), `'delivery-unknown'` (part may be on paper; never resent
 * automatically) or `'duplicate'` (the job id was already accepted).
 */
export interface PrintResult {
  success: boolean;
  cancelled?: boolean;
  error?: string;
  outcome?: 'submitted' | 'not-sent' | 'delivery-unknown' | 'duplicate';
}
